import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import RichTextEditor from './RichTextEditor';
import AdfRenderer from './AdfRenderer';
import { tiptapJsonToAdf, hasRealContent } from '../../utils/adf';
import supportIssuesApi from '../../utils/supportIssuesApi';

const PRIMARY = '#f25d27';
const PRIMARY_DARK = '#d44e1f';

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * @param {{ comment: import('../../types/support').SupportChatMessage }} props
 */
function ChatMessage({ comment }) {
  const isMine = comment.isMine || comment.authorName === 'Cliente';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isMine ? 'flex-end' : 'flex-start',
        mb: 1.25,
      }}
    >
      <Box sx={{ maxWidth: '80%' }}>
        {!isMine && (
          <Typography sx={{ fontSize: '0.72rem', color: '#888', mb: 0.25, fontWeight: 500 }}>
            {comment.authorName}
          </Typography>
        )}
        <Paper
          elevation={0}
          sx={{
            p: '10px 14px',
            borderRadius: isMine ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
            background: isMine ? '#fff3ee' : '#fff',
            border: '1px solid',
            borderColor: isMine ? '#f9c4ad' : '#e0e0e0',
          }}
        >
          {comment.bodyAdf ? (
            <AdfRenderer document={comment.bodyAdf} inheritColor />
          ) : (
            <Typography sx={{ fontSize: '0.88rem', color: '#333', whiteSpace: 'pre-wrap' }}>
              {comment.bodyText || ''}
            </Typography>
          )}

          {/* Anexos do comentário */}
          {comment.attachments && comment.attachments.length > 0 && (
            <Box sx={{ mt: 0.75, pt: 0.5, borderTop: '1px solid #f0f0f0' }}>
              {comment.attachments.map((att) => (
                <Box key={att.id || att.filename} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AttachFileIcon sx={{ fontSize: '0.85rem', color: '#888' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>
                    {att.filename || att.id}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
        <Typography sx={{ fontSize: '0.68rem', color: '#bbb', mt: 0.25, textAlign: isMine ? 'right' : 'left' }}>
          {formatTime(comment.createdAt)}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * @param {{
 *   issue: import('../../types/support').SupportIssueViewModel;
 *   onIssueChange: (issue: import('../../types/support').SupportIssueViewModel) => void;
 * }} props
 */
export default function SupportCommunicationTab({ issue, onIssueChange }) {
  const editorRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(/** @type {string | null} */ (null));

  const sortedMessages = [...(issue.commentsList || [])].sort((a, b) => {
    const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return da - db;
  });

  async function handleSend() {
    if (!issue.issueExternalId) {
      setSendError('Erro ao enviar mensagem.');
      return;
    }

    const json = editorRef.current?.getJson();
    if (!json || !hasRealContent(json)) {
      setSendError('Escreva uma mensagem antes de enviar.');
      return;
    }

    setSending(true);
    setSendError(null);

    const adfBody = tiptapJsonToAdf(json);

    try {
      const resp = await supportIssuesApi.createComment(issue.issueExternalId, {
        request: { body: adfBody },
      });

      const newMessage = /** @type {import('../../types/support').SupportChatMessage} */ ({
        id: resp.external_id ? String(resp.external_id) : `local-${Date.now()}`,
        authorName: 'Cliente',
        createdAt: resp.created_at || new Date().toISOString(),
        bodyAdf: adfBody,
        bodyText: '',
        attachments: [],
        isMine: true,
      });

      const updatedComments = [...(issue.commentsList || []), newMessage];
      onIssueChange({
        ...issue,
        commentsList: updatedComments,
        comments: updatedComments.length,
      });

      editorRef.current?.clear();
    } catch {
      setSendError('Erro ao enviar mensagem.');
    } finally {
      setSending(false);
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 400 }}>
      {/* Histórico */}
      <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, mb: 1.5, minHeight: 200 }}>
        {sortedMessages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: '#aaa', fontSize: '0.88rem' }}>
              Nenhuma mensagem encontrada para este chamado.
            </Typography>
          </Box>
        ) : (
          sortedMessages.map((msg) => (
            <ChatMessage key={msg.id} comment={msg} />
          ))
        )}
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Composer */}
      <Box>
        <RichTextEditor
          ref={editorRef}
          placeholder="Escreva uma mensagem..."
          minHeight={90}
          maxHeight={180}
          onCtrlEnter={handleSend}
        />

        {sendError && (
          <Typography sx={{ fontSize: '0.78rem', color: '#d32f2f', mt: 0.5 }}>
            {sendError}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#bbb' }}>
            Dica: envie com Ctrl+Enter.
          </Typography>
          <Button
            variant="contained"
            size="small"
            endIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
            disabled={sending}
            onClick={handleSend}
            sx={{
              backgroundColor: PRIMARY,
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 500,
              boxShadow: 'none',
              '&:hover': { backgroundColor: PRIMARY_DARK, boxShadow: 'none' },
            }}
          >
            {sending ? 'Enviando...' : 'Enviar'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
